/**
 * Canonical URL → AppView registry.
 *
 * SINGLE SOURCE OF TRUTH for:
 *   • Which paths exist
 *   • Which paths require an authenticated session
 *   • How to resolve a URL into an AppView
 *
 * Before this registry, the lookup tables, protected-route lists, and
 * sign-in redirect maps lived inline inside `AppStateManager.tsx` (in
 * four separate copies) and drifted apart. The drift caused at least
 * two production bugs:
 *
 *   1. `/account-settings` was missing from the `URL_TO_VIEW` table, so
 *      hitting back/forward landed users on the marketing homepage.
 *   2. `/account-settings`, `/my-activities`, `/admin/jobs`, and the
 *      `/dev/*` tools were missing from `protectedRoutes`, so an
 *      unauthenticated user could hit those URLs directly and the
 *      app would render the page without a security challenge.
 *
 * If you add a new top-level URL, ADD IT HERE FIRST. Everything else
 * (AppStateManager, AppRouter, sign-out flow, redirect-after-login,
 * onboarding gate) reads from this file.
 */

import type { AppView } from '../store/AppStateManager';

// ─────────────────────────────────────────────────────────────────────────────
// Public marketing / legal / auth pages — visible without a session.
// Everything not in this set is treated as authenticated.
// ─────────────────────────────────────────────────────────────────────────────
const PUBLIC_EXACT: Record<string, AppView> = {
  '/':                       'home',
  '/login':                  'login',
  '/signup':                 'signup',
  '/signup/consent':         'signup-consent',
  '/forgot-password':        'forgot-password',
  '/reset-password':         'reset-password',
  '/auth/confirm':           'auth-confirm',
  '/privacy-policy':         'privacy-policy',
  '/cookie-policy':          'cookie-policy',
  '/terms-of-service':       'terms-of-service',
  '/gdpr-compliance':        'gdpr-compliance',
  '/dpdp-compliance':        'dpdp-compliance',
  '/trust-center':           'trust-center',
  '/refund-cancellation':    'refund-cancellation',
  '/faqs':                   'faqs',
  '/about-us':               'about-us',
  '/blog':                   'blog',
  '/blog/':                  'blog',
  '/careers':                'careers',
  '/careers/':               'careers',
  '/contact-us':             'contact-us',
  '/email/unsubscribe':      'email-unsubscribe',
  '/pricing':                'pricing',
  '/developers':             'developers',
  '/business':               'business',
  '/job-board':              'job-board',
  '/success-stories':        'success-stories',
  '/features':               'features-overview',
  '/problem':                'problem',
  '/who-its-for':            'who-its-for',
  '/features/smart-matching': 'feature-smart-matching',
  '/features/resume-optimizer': 'feature-resume-optimizer',
  '/features/interview-prep': 'feature-interview-prep',
  '/resume-check':           'resume-check',
  '/debug-logs':             'debug-logs',
  '/payment-success':        'payment-success',
  '/payment-failed':         'payment-failed',
};

// Public dynamic patterns (path starts with prefix, slug follows)
const PUBLIC_DYNAMIC: Array<{ test: (p: string) => boolean; view: AppView }> = [
  { test: (p) => p.startsWith('/blog/') && p.length > '/blog/'.length, view: 'blog-post' },
  { test: (p) => /^\/careers\/[^/]+\/apply$/.test(p),                  view: 'careers-apply' },
  { test: (p) => p.startsWith('/careers/') && p.length > '/careers/'.length, view: 'careers-post' },
  { test: (p) => p.startsWith('/join/'),                               view: 'join-cohort' },
  { test: (p) => p.startsWith('/success/') && p.length > '/success/'.length, view: 'success-story' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Authenticated pages — REQUIRE a session.
//
// Most paths map to the `'dashboard'` AppView (which then routes to the
// correct sub-page via DashboardContainer based on `window.location.pathname`).
// Standalone pages (`/agent`, `/profile`, admin tools) map to their own view.
// ─────────────────────────────────────────────────────────────────────────────
const AUTH_EXACT: Record<string, AppView> = {
  // Top-level workspace
  '/dashboard':              'dashboard',
  '/dashboard-selector':     'dashboard-selector',
  '/agent':                  'agent',
  '/onboarding':             'onboarding',
  '/profile':                'profile',

  // Account hub (was missing — caused back/forward → home bug + security hole)
  '/account-settings':       'dashboard',

  // Dashboard sub-pages (rendered by DashboardContainer based on path)
  '/jobs':                   'dashboard',
  '/history':                'dashboard',
  '/my-activities':          'dashboard',
  '/resume-builder':         'dashboard',
  '/support':                'dashboard',
  '/product-feedback':       'dashboard',
  '/credits':                'dashboard',
  '/prepare-for-interview':  'dashboard',
  '/objectives-tasks':       'dashboard',
  '/collaborate':            'dashboard',
  '/ai-companion':           'dashboard',
  '/calendar-notes':         'dashboard',
  '/developer':              'dashboard',
  '/skill-assessment':       'dashboard',
  '/payment-history':        'dashboard',

  // Payment flow
  '/checkout':               'credits-checkout',
  '/payment-checkout':       'payment-checkout',

  // Referrals
  '/referrals':              'referrals',

  // Admin tools
  '/admin/analytics':        'admin-analytics',
  '/admin/control-panel':    'control-panel',
  '/admin/cron-jobs':        'cron-jobs-admin',
  '/admin/testimonials':     'testimonials-admin',
  '/admin/translations':     'translations-admin',
  '/admin/blogs':            'blog-cms',
  '/admin/blogs/new':        'blog-editor',
  '/admin/jobs':             'dashboard',     // jobs-admin sub-page
  '/dashboard/careers':      'dashboard',     // careers-cms sub-page
  '/dashboard/careers/new':  'dashboard',     // careers-editor sub-page

  // Internal dev tools (DEV-only at render time, but the URL must still be
  // recognised so back/forward doesn't fall through to the homepage)
  '/dev/datatable':          'dev-datatable',
  '/dev/coachmark':          'dev-coachmark',
};

// Authenticated dynamic patterns
const AUTH_DYNAMIC: Array<{ test: (p: string) => boolean; view: AppView }> = [
  { test: (p) => /^\/admin\/blogs\/[^/]+\/edit$/.test(p),              view: 'blog-editor' },
  { test: (p) => p.startsWith('/admin/blogs/'),                        view: 'blog-cms' },
  { test: (p) => p.startsWith('/dashboard/careers/'),                  view: 'dashboard' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Redirects: legacy / deprecated paths.
// `resolveAppViewFromPath` will return BOTH the AppView and the
// canonical path so the caller can `history.replaceState` if needed.
// ─────────────────────────────────────────────────────────────────────────────
const REDIRECTS: Array<{ test: (p: string) => boolean; to: string; view: AppView }> = [
  // The legacy SingleOptimizeFlow was retired — every /optimize/* and
  // /single-optimize/* URL now lives at /agent.
  { test: (p) => p === '/optimize' || p === '/single-optimize'
              || p.startsWith('/optimize/') || p.startsWith('/single-optimize/'),
    to: '/agent', view: 'agent' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface RouteResolution {
  /** The AppView to render. */
  view: AppView;
  /** True if a session is required to access this URL. */
  protected: boolean;
  /** If set, the caller should `replaceState` to this path before rendering. */
  redirectTo?: string;
  /** True when the path doesn't match any known route (renders 404). */
  notFound: boolean;
}

/** Netlify serves marketing URLs with a trailing slash (`/pricing/`). Without
 *  normalization those paths miss PUBLIC_EXACT and render NotFoundPage, which
 *  injects `noindex` — GSC URL Inspection then reports "Excluded by noindex". */
function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/**
 * Resolve a URL pathname into an AppView, including protection flag and
 * any legacy-redirect override. This is the only function callers should
 * use — never inline the lookup tables.
 */
export function resolveAppViewFromPath(path: string): RouteResolution {
  const normalized = normalizePath(path);
  const trailingSlashRedirect = normalized !== path ? normalized : undefined;

  // 1) Deprecated paths take priority
  for (const r of REDIRECTS) {
    if (r.test(normalized)) {
      return {
        view: r.view,
        protected: true,
        redirectTo: r.to,
        notFound: false,
      };
    }
  }

  // 2) Public exact match
  if (normalized in PUBLIC_EXACT) {
    return {
      view: PUBLIC_EXACT[normalized],
      protected: false,
      redirectTo: trailingSlashRedirect,
      notFound: false,
    };
  }

  // 3) Public dynamic match
  for (const r of PUBLIC_DYNAMIC) {
    if (r.test(normalized)) {
      return {
        view: r.view,
        protected: false,
        redirectTo: trailingSlashRedirect,
        notFound: false,
      };
    }
  }

  // 4) Authenticated exact match
  if (normalized in AUTH_EXACT) {
    return {
      view: AUTH_EXACT[normalized],
      protected: true,
      redirectTo: trailingSlashRedirect,
      notFound: false,
    };
  }

  // 5) Authenticated dynamic match
  for (const r of AUTH_DYNAMIC) {
    if (r.test(normalized)) {
      return {
        view: r.view,
        protected: true,
        redirectTo: trailingSlashRedirect,
        notFound: false,
      };
    }
  }

  // 6) Unknown path → 404 (NEVER silently fall back to 'home', which would
  //    render the landing page at e.g. /typo-here and confuse users into
  //    thinking they were signed out)
  return { view: 'not-found', protected: false, notFound: true };
}

/** True if the URL pathname requires an authenticated session. */
export function isProtectedRoute(path: string): boolean {
  return resolveAppViewFromPath(path).protected;
}

/** Canonical URL for an AppView. Falls back to the current pathname for
 *  views that carry a slug in the URL itself (blog-post, careers-post, etc). */
export function appViewToPath(view: AppView, currentPath?: string): string {
  // Authenticated views
  for (const [p, v] of Object.entries(AUTH_EXACT)) {
    if (v === view) return p;
  }
  // Public views
  for (const [p, v] of Object.entries(PUBLIC_EXACT)) {
    if (v === view) return p;
  }
  // Dynamic / slug-carrying views — keep the current URL
  return currentPath ?? '/';
}

/** All authenticated path *prefixes* (used by the secondary security guard
 *  to validate that an in-flight URL change isn't escaping into a public
 *  route without a session). */
export const AUTH_PATH_PREFIXES: readonly string[] = Object.freeze([
  ...Object.keys(AUTH_EXACT),
  '/admin/blogs/',
  '/dashboard/careers/',
]);
